"use client";

import { useRef, useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  X, CloudUpload,
  CheckCircle2, Loader2, Hash, Calendar,
  CheckSquare, Link, Mail, Phone, Type, ChevronDown, Tags, User, Check, CircleDot,
} from "lucide-react";
import { toast } from "@/lib/toast";
import { importFile, listTrelloBoards, listAsanaProjects, listConfluenceSpaces, importTrelloBoard, importAsanaProject, importConfluenceSpace, listSyncProjects, createSyncedDatabase, type SyncProvider, type ImportResult } from "@/services/exportImportService";
import { BrandIcon } from "@/components/tailwind/ui/BrandIcon";
import { useWorkspaceContext } from "@/contexts/workspaceContext";
import { FORMAT_CARDS } from "@/components/tailwind/import/formatCards";
import { useBlockActions } from "@/contexts/blockContext";
import { useBoard } from "@/contexts/boardContext";
import { AnchoredPopover } from "@/components/tailwind/ui/anchored-popover";
import { fetchNote } from "@/services-frontend/note/notesService";
import {
  appDB,
  addPrivatePageMutation,
  addPublicPageMutation,
  addWorkAreaPageMutation,
  upsertBlocksMutation,
} from "@/indexedDB";
import type { Block } from "@/types/block";

// ── Types ─────────────────────────────────────────────────────────────────────

type ColumnType =
  | "text" | "number" | "date" | "checkbox" | "status"
  | "select" | "multi_select" | "url" | "email" | "phone" | "person";

interface CsvPreview {
  file: File;
  rawRows: string[][];
  dbTitle: string;
  headers: string[];
  columnTypes: ColumnType[];
  totalRows: number;
  useFirstRowAsHeader: boolean;
}

interface ImportModalProps {
  onClose: () => void;
  parentId: string;
  parentTable?: string;
  workareaId?: string;
  onImportSuccess?: (result: ImportResult) => void;
  initialFile?: File;
}

// ── Column type config ────────────────────────────────────────────────────────

const TYPE_OPTIONS: { value: ColumnType; label: string; Icon: React.ElementType }[] = [
  { value: "text",         label: "Text",         Icon: Type },
  { value: "person",       label: "Person",       Icon: User },
  { value: "status",       label: "Status",       Icon: CircleDot },
  { value: "number",       label: "Number",       Icon: Hash },
  { value: "date",         label: "Date",         Icon: Calendar },
  { value: "checkbox",     label: "Checkbox",     Icon: CheckSquare },
  { value: "select",       label: "Select",       Icon: ChevronDown },
  { value: "multi_select", label: "Multi-select", Icon: Tags },
  { value: "url",          label: "URL",          Icon: Link },
  { value: "email",        label: "Email",        Icon: Mail },
  { value: "phone",        label: "Phone",        Icon: Phone },
];

// ── Client-side CSV parser (preview only) ─────────────────────────────────────

// Picks a delimiter by scanning the first row, ignoring separators inside quotes.
function detectDelimiter(text: string): string {
  let inQuotes = false;
  let firstRow = "";
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (ch === '"') inQuotes = !inQuotes;
    else if (!inQuotes && (ch === "\n" || ch === "\r")) break;
    firstRow += ch;
  }
  const t = (firstRow.match(/\t/g) || []).length;
  const s = (firstRow.match(/;/g) || []).length;
  const c = (firstRow.match(/,/g) || []).length;
  if (t > c && t > s) return "\t";
  if (s > c) return ";";
  return ",";
}

// RFC 4180-style parse: handles quoted cells with embedded delimiters/newlines.
function parseCsv(text: string, delimiter: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;

  const pushCell = () => { row.push(cell.trim()); cell = ""; };
  const pushRow = () => {
    pushCell();
    if (row.some(v => v !== "")) rows.push(row);
    row = [];
  };

  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') { cell += '"'; i++; }
      else if (ch === '"') { inQuotes = false; }
      else { cell += ch; }
    } else {
      if (ch === '"') inQuotes = true;
      else if (ch === delimiter) pushCell();
      else if (ch === "\r") { if (text[i + 1] === "\n") i++; pushRow(); }
      else if (ch === "\n") pushRow();
      else cell += ch;
    }
  }
  if (cell !== "" || row.length > 0) pushRow();
  return rows;
}

function inferType(values: string[]): ColumnType {
  const nonempty = values.filter(v => v !== "");
  if (nonempty.length === 0) return "text";
  // Only classify as checkbox when at least one token is a clear boolean —
  // prevents misclassifying columns of 0/1 quantities.
  const boolRe = /^(true|false|yes|no|✓|✗)$/i;
  const boolOrBit = /^(true|false|yes|no|1|0|✓|✗)$/i;
  if (nonempty.some(v => boolRe.test(v)) && nonempty.every(v => boolOrBit.test(v))) return "checkbox";
  if (nonempty.every(v => !isNaN(Number(v.replace(/,/g, ""))) && v !== "")) return "number";
  if (nonempty.every(v => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v))) return "email";
  if (nonempty.every(v => /^https?:\/\/.{2,}/i.test(v))) return "url";
  if (nonempty.every(v => /^(\d{4}[-/.]\d{1,2}[-/.]\d{1,2}|\d{1,2}[-/.]\d{1,2}[-/.]\d{2,4})/.test(v) && isNaN(Number(v)))) return "date";
  const withCommas = nonempty.filter(v => v.includes(","));
  if (withCommas.length > nonempty.length * 0.3) {
    const allParts = nonempty.flatMap(v => v.split(",").map(p => p.trim()).filter(Boolean));
    if (new Set(allParts).size <= 20) return "multi_select";
  }
  const unique = new Set(nonempty);
  if (unique.size <= 10 || (unique.size <= 20 && unique.size <= nonempty.length * 0.4)) return "select";
  return "text";
}

function deriveFromRawRows(rawRows: string[][], useFirst: boolean) {
  if (rawRows.length === 0) return { headers: [], columnTypes: [] as ColumnType[], totalRows: 0 };
  const headers = useFirst
    ? (rawRows[0] ?? []).map((h, i) => h.trim() || `Column ${i + 1}`)
    : Array.from({ length: (rawRows[0] ?? []).length }, (_, i) => `Column ${i + 1}`);
  const dataRows = useFirst ? rawRows.slice(1) : rawRows;
  const colValues = headers.map((_, i) => dataRows.slice(0, 200).map(r => (r[i] ?? "").trim()));
  const columnTypes: ColumnType[] = headers.map((_, i) => i === 0 ? "text" : inferType(colValues[i]!));
  return { headers, columnTypes, totalRows: dataRows.length };
}

function parseCsvPreview(text: string): Omit<CsvPreview, "file" | "dbTitle"> {
  const content = text.startsWith("﻿") ? text.slice(1) : text;
  if (content.trim() === "") {
    return { rawRows: [], headers: [], columnTypes: [], totalRows: 0, useFirstRowAsHeader: true };
  }
  const delimiter = detectDelimiter(content);
  const rawRows = parseCsv(content, delimiter);
  const { headers, columnTypes, totalRows } = deriveFromRawRows(rawRows, true);
  return { rawRows, headers, columnTypes, totalRows, useFirstRowAsHeader: true };
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ImportModal({
  onClose,
  parentId,
  parentTable = "workspace",
  workareaId,
  onImportSuccess,
  initialFile,
}: ImportModalProps) {
  const { currentWorkspace } = useWorkspaceContext();
  const workspaceId = currentWorkspace?._id || "";
  // Block context hooks for the post-import refresh — pulls the new block
  // into the in-memory map and updates the parent's blockIds when the import
  // target is a page.
  const { addBlock, upsertBlocks, getBlock, updateBlock } = useBlockActions();
  // Board context for the "collection" branch — when an import targets a
  // database row, we mirror the new page id into the parent dataSource's
  // blockIds so the board grid picks it up without a reload.
  const { dataSources, updateDataSource } = useBoard();

  const [isDragging, setIsDragging]     = useState(false);
  const [isImporting, setIsImporting]   = useState(false);
  const [result, setResult]             = useState<ImportResult | null>(null);
  const [csvPreview, setCsvPreview]     = useState<CsvPreview | null>(null);
  const [dropdownAnchor, setDropdownAnchor] = useState<{
    colIdx: number; el: HTMLElement;
  } | null>(null);

  const dropInputRef  = useRef<HTMLInputElement>(null);
  const cardInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const initialFileProcessed = useRef(false);

  // ── Helpers ──────────────────────────────────────────────────────────────────

  /**
   * The import API only creates the new block(s) on the server — it doesn't
   * touch the client's block context, IndexedDB, or sidebar order tables.
   * Result: the imported page was invisible in the sidebar until a hard
   * reload picked it up via the next workspace sync.
   *
   * This helper bridges that gap. After a successful import we:
   *   1. Fetch the new block tree via /api/note/block/get-all-block/:id
   *   2. Persist all blocks to IDB via upsertBlocksMutation (isSync: 1 —
   *      server already owns them, no SYNC op needed)
   *   3. Mirror into the global blocks context so any subscriber re-renders
   *   4. Update the appropriate sidebar surface based on `parentTable`:
   *        - "workspace" root  → private / public / restricted section order
   *        - "workarea"        → workarea-scoped sidebar order
   *        - "page" child      → append to parent's blockIds (context + IDB)
   *        - "collection" row  → append to parent dataSource's blockIds
   *                              (board context + IDB)
   *      All sidebar mutations pass skipSync=true because the import API
   *      already registered the new page server-side.
   */
  const refreshClientStateAfterImport = useCallback(async (newPageId: string) => {
    try {
      const response: any = await fetchNote(newPageId);
      if (!response || ("isError" in response && response.isError)) return;

      const blocks: Block[] = response?.blocks || [];
      const newBlock = blocks.find((b) => b._id === newPageId) ?? blocks[0];
      if (!newBlock) return;

      // 1. Persist the whole tree to IDB. upsertBlocksMutation guards against
      //    clobbering any unsynced local edits.
      if (blocks.length > 0) {
        await upsertBlocksMutation(blocks, newPageId);
      }

      // 2. Mirror into context. Use upsertBlocks if available (bulk), fall
      //    back to addBlock for the root.
      if (blocks.length > 0 && typeof upsertBlocks === "function") {
        upsertBlocks(blocks);
      } else {
        addBlock(newBlock);
      }

      // 3. Sidebar-order placement.
      if (parentTable === "workspace") {
        const pageType = ((newBlock.value as any)?.pageType ?? "private") as string;
        if (pageType === "public" || pageType === "restricted") {
          await addPublicPageMutation(newPageId, workspaceId, undefined, true);
        } else {
          await addPrivatePageMutation(newPageId, workspaceId, undefined, true);
        }
      } else if (parentTable === "workarea" && workareaId) {
        await addWorkAreaPageMutation(workareaId, newPageId, workspaceId, undefined, true);
      } else if (parentTable === "page") {
        // Child page → append to parent's blockIds in both context and IDB.
        const parentInContext = getBlock(parentId);
        if (parentInContext) {
          const existing = parentInContext.blockIds ?? [];
          if (!existing.includes(newPageId)) {
            updateBlock(parentId, { blockIds: [...existing, newPageId] });
          }
        }
        const parentRecord = await appDB.blocks.get(parentId);
        if (parentRecord) {
          const existingIds = parentRecord.blockIds ?? [];
          if (!existingIds.includes(newPageId)) {
            await appDB.blocks.put({
              ...parentRecord,
              blockIds: [...existingIds, newPageId],
              isSync: 0 as 0,
              localUpdatedAt: Date.now(),
            });
          }
        }
      } else if (parentTable === "collection") {
        // Database row → mirror into the parent dataSource's blockIds so the
        // board grid renders the new row immediately. Mirrors the duplicate
        // flow's collection handling.
        const ds = dataSources[parentId];
        if (ds) {
          const existing = ds.blockIds ?? [];
          if (!existing.includes(newPageId)) {
            const nextBlockIds = [...existing, newPageId];
            updateDataSource(parentId, { blockIds: nextBlockIds });
            await appDB.dataSources.update(parentId, {
              blockIds: nextBlockIds,
              isSync: 0 as 0,
              localUpdatedAt: Date.now(),
            });
          }
        }
      }
    } catch (err) {
      // Non-fatal: the import succeeded server-side. Worst case the user has
      // to reload to see the new page. Log so we can debug if it happens.
      console.error("[ImportModal] Post-import client refresh failed:", err);
    }
  }, [
    parentTable, parentId, workspaceId, workareaId,
    addBlock, upsertBlocks, getBlock, updateBlock,
    dataSources, updateDataSource,
  ]);

  const runImport = useCallback(async (
    file: File,
    columnTypes?: Record<number, string>,
    databaseTitle?: string,
  ) => {
    if (!workspaceId) { toast.error("No workspace selected"); return; }
    setIsImporting(true);
    const res = await importFile(file, parentId, parentTable, workspaceId, {
      workareaId,
      columnTypes,
      databaseTitle,
    });
    setIsImporting(false);
    if (res) {
      setResult(res);
      onImportSuccess?.(res);
      // Hydrate the new block(s) into context + IDB + sidebar so the page
      // shows up in the sidebar without a manual reload.
      const newId = res.pageId || res.databaseId;
      if (newId) {
        await refreshClientStateAfterImport(newId);
      }
    }
  }, [workspaceId, parentId, parentTable, workareaId, onImportSuccess, refreshClientStateAfterImport]);

  // ── App imports (Trello / Asana) ────────────────────────────────────────────

  /**
   * One picker shell serves both flows. `mode` disambiguates because Asana
   * appears in each: importing a project copies it once, syncing it mirrors
   * it continuously. They must never be conflated in the UI.
   */
  const [appPicker, setAppPicker] = useState<null | {
    mode: "import" | "sync";
    provider: "trello" | "asana" | "confluence" | "jira" | "gitlab";
    loading: boolean;
    connected: boolean;
    truncated?: boolean;
    items: Array<{ id: string; name: string; subtitle?: string }>;
  }>(null);

  const openAppPicker = useCallback(async (provider: "trello" | "asana" | "confluence") => {
    if (!workspaceId) { toast.error("No workspace selected"); return; }
    setAppPicker({ mode: "import", provider, loading: true, connected: true, items: [] });
    const applyIfCurrent = (
      next: { connected: boolean; items: Array<{ id: string; name: string; subtitle?: string }> },
    ) =>
      setAppPicker(prev =>
        prev && prev.mode === "import" && prev.provider === provider
          ? { mode: "import", provider, loading: false, ...next }
          : prev,
      );

    if (provider === "trello") {
      const { connected, boards } = await listTrelloBoards(workspaceId);
      applyIfCurrent({ connected, items: boards.map(b => ({ id: b.shortLink, name: b.name })) });
    } else if (provider === "confluence") {
      const { connected, spaces } = await listConfluenceSpaces(workspaceId);
      applyIfCurrent({
        connected,
        // cloudId rides along in the id — split again at import time.
        items: spaces.map(sp => ({ id: `${sp.cloudId}:${sp.spaceId}`, name: sp.name, subtitle: sp.siteName })),
      });
    } else {
      const { connected, projects } = await listAsanaProjects(workspaceId);
      applyIfCurrent({
        connected,
        items: projects.map(pr => ({ id: pr.gid, name: pr.name, subtitle: pr.workspaceName })),
      });
    }
  }, [workspaceId]);

  const runAppImport = useCallback(async (provider: "trello" | "asana" | "confluence", itemId: string) => {
    if (!workspaceId) { toast.error("No workspace selected"); return; }
    setAppPicker(null);
    setIsImporting(true);
    let res: ImportResult | null;
    if (provider === "confluence") {
      const [cloudId, spaceId] = itemId.split(":");
      res = await importConfluenceSpace({ cloudId: cloudId!, spaceId: spaceId!, parentId, parentTable, workspaceId, workareaId });
    } else {
      res = provider === "trello"
        ? await importTrelloBoard({ boardShortLink: itemId, parentId, parentTable, workspaceId, workareaId })
        : await importAsanaProject({ projectGid: itemId, parentId, parentTable, workspaceId, workareaId });
    }
    setIsImporting(false);
    if (res) {
      setResult(res);
      onImportSuccess?.(res);
      const newId = res.pageId || res.databaseId;
      if (newId) {
        await refreshClientStateAfterImport(newId);
      }
    }
  }, [workspaceId, parentId, parentTable, workareaId, onImportSuccess, refreshClientStateAfterImport]);

  // ── Synced databases (Jira / GitLab / Asana) ───────────────────────────────
  // A sync is not an import: it creates a database that keeps updating.

  const openSyncPicker = useCallback(async (provider: SyncProvider) => {
    if (!workspaceId) { toast.error("No workspace selected"); return; }
    setAppPicker({ mode: "sync", provider, loading: true, connected: true, items: [] });

    const { connected, items, truncated } = await listSyncProjects(provider, workspaceId);
    setAppPicker(prev =>
      prev && prev.mode === "sync" && prev.provider === provider
        ? {
            mode: "sync",
            provider,
            loading: false,
            connected,
            truncated,
            // remoteKey is opaque and provider-specific — carried through
            // untouched so nobody has to reassemble it.
            items: items.map(i => ({ id: i.remoteKey, name: i.name, subtitle: i.subtitle })),
          }
        : prev,
    );
  }, [workspaceId]);

  const runSyncCreate = useCallback(async (provider: SyncProvider, remoteKey: string) => {
    if (!workspaceId) { toast.error("No workspace selected"); return; }
    setAppPicker(null);
    setIsImporting(true);
    const res = await createSyncedDatabase({
      connectorId: provider, remoteKey, parentId, parentTable, workspaceId, workareaId,
    });
    setIsImporting(false);
    if (!res) return;

    // A run can legitimately stop at a safety cap. Saying "Synced" here
    // would repeat exactly the silent-truncation bug the connectors just
    // had removed, so the server's own explanation is shown instead.
    if (res.run?.status === "partial" && res.run.message) {
      toast.warning(res.run.message, { duration: 10000 });
    } else if (res.run?.status === "error") {
      toast.error(res.run.message || "The first sync failed — open the database and retry.");
    } else {
      toast.success(`${res.remoteName} is now syncing into this workspace`);
    }

    if (res.pageId) await refreshClientStateAfterImport(res.pageId);
    onImportSuccess?.({ pageId: res.pageId, databaseId: res.dataSourceId } as ImportResult);
  }, [workspaceId, parentId, parentTable, workareaId, onImportSuccess, refreshClientStateAfterImport]);

  const pickFile = useCallback(async (file: File) => {
    const ext = `.${file.name.split(".").pop()?.toLowerCase()}`;
    const valid = [".zip", ".csv", ".md", ".txt", ".html", ".htm", ".pdf", ".docx", ".enex"];
    if (!valid.includes(ext)) { toast.error(`Unsupported type: ${ext}`); return; }

    if (ext === ".csv") {
      try {
        const text = await file.text();
        const preview = parseCsvPreview(text);
        if (preview.headers.length === 0) { toast.error("CSV file appears to be empty"); return; }
        setCsvPreview({
          file,
          dbTitle: file.name.replace(/\.csv$/i, "") || "Imported Database",
          ...preview,
        });
      } catch {
        toast.error("Could not read CSV file");
      }
      return;
    }

    runImport(file);
  }, [runImport]);

  // Auto-process a pre-selected file passed in from outside (e.g. settings page).
  // Mount-only by design: callers are expected to remount the modal with a new
  // `initialFile` rather than swap it in place. The ref guards against re-firing
  // if React Strict Mode double-invokes the effect.
  useEffect(() => {
    if (initialFile && !initialFileProcessed.current) {
      initialFileProcessed.current = true;
      pickFile(initialFile);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDragOver  = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); };
  const onDragLeave = () => setIsDragging(false);
  const onDrop      = (e: React.DragEvent) => {
    e.preventDefault(); setIsDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) pickFile(f);
  };

  // ── Success screen ────────────────────────────────────────────────────────────

  if (result) {
    const detail =
      result.blockCount != null ? `${result.blockCount} blocks created` :
      result.rowCount   != null ? `${result.rowCount} rows imported`    :
      result.pageCount  != null ? `${result.pageCount} pages created`   : "";

    return createPortal(
      // Bottom-anchored on mobile (bottom sheet), centered on desktop.
      <div data-import-modal="true" className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-[9999]" onClick={onClose}>
        <div
          // Mobile: full width, top corners only, max 90dvh + scroll.
          // Desktop: original 420px width, fully rounded.
          className="bg-white dark:bg-zinc-900 shadow-2xl p-8 flex flex-col items-center gap-4 w-full md:w-[420px] rounded-t-2xl md:rounded-2xl max-h-[90dvh] md:max-h-none overflow-y-auto md:overflow-visible"
          onClick={e => e.stopPropagation()}
        >
          <CheckCircle2 className="h-12 w-12 text-green-500" />
          <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">Import complete!</h2>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 text-center">
            <span className="font-medium text-zinc-800 dark:text-zinc-200">"{result.title}"</span> was imported.
            {detail && ` ${detail}.`}
          </p>
          <button
            type="button"
            className="mt-1 px-6 py-2 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-lg text-sm font-medium hover-component  transition-colors"
            onClick={onClose}
          >
            Done
          </button>
        </div>
      </div>,
      document.body,
    );
  }

  // ── CSV setup screen ──────────────────────────────────────────────────────────

  if (csvPreview && !isImporting) {
    const toggleFirstRowHeader = () => {
      setCsvPreview(prev => {
        if (!prev) return prev;
        const useFirst = !prev.useFirstRowAsHeader;
        const { headers, columnTypes, totalRows } = deriveFromRawRows(prev.rawRows, useFirst);
        return { ...prev, useFirstRowAsHeader: useFirst, headers, columnTypes, totalRows };
      });
    };

    const updateType = (colIdx: number, type: ColumnType) => {
      setCsvPreview(prev => {
        if (!prev) return prev;
        const newTypes = [...prev.columnTypes];
        newTypes[colIdx] = type;
        return { ...prev, columnTypes: newTypes };
      });
      setDropdownAnchor(null);
    };

    const handleImport = () => {
      const overrides: Record<number, string> = {};
      csvPreview.columnTypes.forEach((t, i) => { if (i > 0) overrides[i] = t; });
      runImport(csvPreview.file, overrides, csvPreview.dbTitle);
      setCsvPreview(null);
    };

    // Active column type for the open dropdown
    const activeType = dropdownAnchor != null
      ? (csvPreview.columnTypes[dropdownAnchor.colIdx] ?? "text")
      : "text";

    // Two sibling portals — never nest portals inside each other
    return (
      <>
        {createPortal(
          <div
            data-import-modal="true"
            // Bottom-anchored on mobile, centered on desktop.
            className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-[9999]"
            onClick={e => { if (e.target === e.currentTarget) { setCsvPreview(null); setDropdownAnchor(null); } }}
          >
            <div
              // Mobile: full width, top corners only, max 90dvh — the CSV
              // preview grid inside is already scrollable.
              // Desktop: original 480px width with viewport caps, fully rounded.
              className="bg-white dark:bg-zinc-900 shadow-2xl flex flex-col w-full md:w-[480px] md:max-w-[calc(100vw-32px)] rounded-t-2xl md:rounded-2xl max-h-[90dvh] md:max-h-[90vh]"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-6 pt-6 pb-4 flex-shrink-0 border-b border-zinc-100 dark:border-zinc-800">
                <div className="flex items-start justify-between mb-1">
                  <h2 className="text-[17px] font-semibold text-zinc-900 dark:text-zinc-100">Setup CSV columns</h2>
                  <button type="button" onClick={onClose} className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors mt-0.5 ml-3">
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <p className="text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed">
                  Choose the property type for each column in your CSV file
                </p>
              </div>

              <div className="flex flex-col gap-5 px-6 py-5 overflow-y-auto flex-1">

                {/* Database name */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-[11px] font-semibold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider">
                    Database name
                  </label>
                  <input
                    type="text"
                    value={csvPreview.dbTitle}
                    onChange={e => setCsvPreview(prev => prev ? { ...prev, dbTitle: e.target.value } : prev)}
                    className="w-full px-3 py-2 rounded-lg border border-component bg-white dark:bg-zinc-800 text-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-400 transition-colors"
                    placeholder="Database name"
                  />
                </div>

                {/* Use first row as headers toggle */}
                <button
                  type="button"
                  onClick={toggleFirstRowHeader}
                  className="flex items-center gap-3 w-fit select-none"
                >
                  <div
                    className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${
                      csvPreview.useFirstRowAsHeader ? "bg-blue-500" : "bg-zinc-300 dark:bg-zinc-600"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                        csvPreview.useFirstRowAsHeader ? "translate-x-4" : "translate-x-0"
                      }`}
                    />
                  </div>
                  <span className="text-sm text-zinc-700 dark:text-zinc-300">Use first row as headers</span>
                </button>

                {/* Column list */}
                <div className="flex flex-col gap-2">
                  <div className="grid grid-cols-[1fr_16px_160px] gap-3 px-1">
                    <span className="text-[11px] font-semibold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider">CSV File Columns</span>
                    <span />
                    <span className="text-[11px] font-semibold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider">Property Type</span>
                  </div>

                  <div className="flex flex-col border border-component rounded-xl divide-y divide-zinc-100 dark:divide-zinc-800">
                    {csvPreview.headers.map((header, colIdx) => {
                      const type = csvPreview.columnTypes[colIdx] ?? "text";
                      const typeConfig = TYPE_OPTIONS.find(t => t.value === type)!;
                      const TypeIcon = typeConfig.Icon;
                      const isTitle = colIdx === 0;
                      const isOpen = dropdownAnchor?.colIdx === colIdx;

                      return (
                        <div
                          key={colIdx}
                          className="grid grid-cols-[1fr_16px_160px] gap-3 items-center px-3 py-2.5 hover-component  transition-colors"
                        >
                          <span className="text-sm text-zinc-800 dark:text-zinc-200 truncate font-medium">
                            {header || `Column ${colIdx + 1}`}
                          </span>
                          <span className="text-zinc-300 dark:text-zinc-600 text-xs font-light">→</span>
                          <button
                            type="button"
                            disabled={isTitle}
                            onClick={e => {
                              if (isTitle) return;
                              if (isOpen) { setDropdownAnchor(null); return; }
                              setDropdownAnchor({ colIdx, el: e.currentTarget as HTMLElement });
                            }}
                            className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                              isTitle
                                ? "border-component bg-zinc-50 dark:bg-zinc-800/60 text-zinc-500 cursor-default"
                                : isOpen
                                ? "border-blue-400 dark:border-blue-500 bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-500"
                                : "border-component bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-300 hover:border-zinc-300 dark:hover:border-zinc-600 hover-component  cursor-pointer"
                            }`}
                          >
                            <TypeIcon className="h-3.5 w-3.5 flex-shrink-0" />
                            <span className="flex-1 text-left truncate">{isTitle ? "Title" : typeConfig.label}</span>
                            {!isTitle && <ChevronDown className={`h-3 w-3 flex-shrink-0 opacity-60 transition-transform ${isOpen ? "rotate-180" : ""}`} />}
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  <p className="text-xs text-zinc-400 dark:text-zinc-500 px-1">
                    {csvPreview.totalRows.toLocaleString()} row{csvPreview.totalRows !== 1 ? "s" : ""} detected
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 pb-6 pt-2 flex flex-col gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={handleImport}
                  className="w-full py-2.5 rounded-xl text-sm font-semibold bg-blue-500 hover:bg-blue-700 active:bg-blue-800 text-white transition-colors"
                >
                  Import CSV
                </button>
                <button
                  type="button"
                  onClick={() => { setCsvPreview(null); setDropdownAnchor(null); }}
                  className="w-full py-2 rounded-xl text-sm font-medium text-zinc-500 dark:text-zinc-400 hover-component  transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

        {dropdownAnchor && (
          <AnchoredPopover
            anchor={dropdownAnchor.el}
            onClose={() => setDropdownAnchor(null)}
            zIndex={10001}
            className="bg-white dark:bg-zinc-800 border border-component rounded-xl shadow-xl py-1.5 min-w-[180px]"
          >
            {/* data-import-modal: SettingsModal's outside-click ignores this. */}
            <div data-import-modal="true">
              {TYPE_OPTIONS.map(opt => {
                const OptIcon = opt.Icon;
                const isActive = activeType === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => updateType(dropdownAnchor.colIdx, opt.value)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors text-left ${
                      isActive
                        ? "bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-500"
                        : "text-zinc-700 dark:text-zinc-300 hover-component "
                    }`}
                  >
                    <OptIcon className="h-3.5 w-3.5 flex-shrink-0 text-zinc-500 dark:text-zinc-400" />
                    <span className="flex-1">{opt.label}</span>
                    {isActive && <Check className="h-3 w-3 flex-shrink-0 text-blue-500" />}
                  </button>
                );
              })}
            </div>
          </AnchoredPopover>
        )}
      </>
    );
  }

  // ── Importing spinner ─────────────────────────────────────────────────────────

  // ── App picker (Trello boards / Asana projects) ──────────────────────────────

  if (appPicker) {
    const label =
      appPicker.provider === "trello" ? "Trello"
      : appPicker.provider === "confluence" ? "Confluence"
      : appPicker.provider === "jira" ? "Jira"
      : appPicker.provider === "gitlab" ? "GitLab"
      : "Asana";
    const noun =
      appPicker.provider === "trello" ? "board"
      : appPicker.provider === "confluence" ? "space"
      : "project";
    const isSync = appPicker.mode === "sync";
    return createPortal(
      <div
        data-import-modal="true"
        className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-[9999]"
        onClick={e => { if (e.target === e.currentTarget) setAppPicker(null); }}
      >
        <div className="bg-white dark:bg-zinc-900 shadow-2xl w-full md:w-[440px] md:max-w-[calc(100vw-32px)] rounded-t-2xl md:rounded-2xl max-h-[80dvh] overflow-y-auto">
          <div className="flex items-center justify-between px-6 pt-5 pb-4">
            <div className="flex items-center gap-2.5">
              <BrandIcon id={appPicker.provider} alt={label} />
              <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                {isSync ? `Sync a ${label} ${noun}` : `Import a ${label} ${noun}`}
              </h2>
            </div>
            <button type="button" onClick={() => setAppPicker(null)} className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="px-6 pb-6">
            {appPicker.loading ? (
              <div className="flex items-center justify-center py-10 text-zinc-400">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : !appPicker.connected ? (
              <p className="text-sm text-zinc-500 dark:text-zinc-400 py-6 text-center">
                {label} isn&apos;t connected yet. Connect it under Settings → Connections, then come back here.
              </p>
            ) : appPicker.items.length === 0 ? (
              <p className="text-sm text-zinc-500 dark:text-zinc-400 py-6 text-center">
                No {noun}s found in your {label} account.
              </p>
            ) : (
              <div className="flex flex-col gap-1.5">
                {appPicker.truncated && (
                  // Never present a prefix as the whole list — that is the
                  // bug that left someone unable to find their project with
                  // no explanation.
                  <p className="text-xs text-amber-600 dark:text-amber-500 pb-1 leading-snug">
                    Showing the first {appPicker.items.length}. You have more {noun}s than we can list here —
                    search isn&apos;t wired up yet, so if yours is missing, that&apos;s why.
                  </p>
                )}
                {appPicker.items.map(item => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() =>
                      appPicker.mode === "sync"
                        ? runSyncCreate(appPicker.provider as SyncProvider, item.id)
                        : runAppImport(
                            appPicker.provider as "trello" | "asana" | "confluence",
                            item.id,
                          )
                    }
                    className="flex flex-col items-start px-3 py-2.5 rounded-xl border border-component hover:border-zinc-300 dark:hover:border-zinc-600 hover-component transition-colors text-left"
                  >
                    <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate w-full">{item.name}</span>
                    {item.subtitle && (
                      <span className="text-xs text-zinc-500 dark:text-zinc-400">{item.subtitle}</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  // ── Main pick screen ──────────────────────────────────────────────────────────

  return createPortal(
    <div
      data-import-modal="true"
      // Bottom-anchored on mobile, centered on desktop.
      className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-[9999]"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white dark:bg-zinc-900 shadow-2xl w-full md:w-[540px] md:max-w-[calc(100vw-32px)] rounded-t-2xl md:rounded-2xl max-h-[90dvh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4">
          {/* "and sync": the modal also creates synced databases now (the
              "Keep in sync" section below) — Atharv's review ask on #277.
              The drop-zone copy further down deliberately still says
              "Import your content": dropping a file only ever imports. */}
          <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">Import and sync your content</h2>
          <button type="button" onClick={onClose} className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-6 pb-6 flex flex-col gap-5">

          {/* Drop zone */}
          <input
            ref={dropInputRef}
            type="file"
            accept=".zip,.csv,.md,.txt,.html,.htm,.pdf,.docx,.enex"
            className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) pickFile(f); e.target.value = ""; }}
          />
          <div
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            className={`rounded-xl border-2 border-dashed transition-colors px-6 py-8 text-center ${
              isDragging
                ? "border-blue-400 bg-blue-50 dark:bg-blue-950/20"
                : isImporting
                ? "border-component bg-zinc-50 dark:bg-zinc-800/40"
                : "border-component hover:border-blue-300 dark:hover:border-blue-700 hover:bg-blue-50/30 dark:hover:bg-blue-950/10"
            }`}
          >
            {isImporting ? (
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-8 w-8 text-blue-400 animate-spin" />
                <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">Importing…</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <CloudUpload className="h-8 w-8 text-blue-400 mb-1" strokeWidth={1.5} />
                <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Import your content</p>
                <p className="text-sm text-zinc-500 dark:text-zinc-400">
                  Drag and drop ZIP, CSV, PDF, text, markdown, or HTML files, or{" "}
                  <button
                    type="button"
                    className="text-blue-500 hover:underline focus:outline-none"
                    onClick={() => dropInputRef.current?.click()}
                  >
                    choose a file
                  </button>
                </p>
                <p className="text-xs text-zinc-400 mt-0.5">ZIP files can be a maximum of 50 MB</p>
              </div>
            )}
          </div>

          {/* File-based imports */}
          <div>
            <p className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wide mb-3">
              Import from apps
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                disabled={isImporting}
                onClick={() => openAppPicker("trello")}
                className="flex items-start gap-3 p-3 rounded-xl border border-component hover:border-zinc-300 dark:hover:border-zinc-600 hover-component transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-sky-50 dark:bg-sky-950/30">
                  <BrandIcon id="trello" alt="Trello" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Trello</p>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 leading-snug mt-0.5">Import a board as a database</p>
                </div>
              </button>
              <button
                type="button"
                disabled={isImporting}
                onClick={() => openAppPicker("asana")}
                className="flex items-start gap-3 p-3 rounded-xl border border-component hover:border-zinc-300 dark:hover:border-zinc-600 hover-component transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-rose-50 dark:bg-rose-950/30">
                  <BrandIcon id="asana" alt="Asana" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Asana</p>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 leading-snug mt-0.5">Import a project as a database</p>
                </div>
              </button>
              <button
                type="button"
                disabled={isImporting}
                onClick={() => openAppPicker("confluence")}
                className="flex items-start gap-3 p-3 rounded-xl border border-component hover:border-zinc-300 dark:hover:border-zinc-600 hover-component transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-blue-50 dark:bg-blue-950/30">
                  <BrandIcon id="confluence" alt="Confluence" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Confluence</p>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 leading-snug mt-0.5">Import a space as nested pages</p>
                </div>
              </button>
            </div>
          </div>

          {/*
            Deliberately its own section, not folded into "Import from apps".
            An import is a one-time copy you then own and edit; a sync is a
            mirror that keeps changing under you. Presenting them as the
            same action is how someone edits a row and loses it on the next
            sweep.
          */}
          <div>
            <p className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wide mb-3">
              Keep in sync
            </p>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 -mt-2 mb-3 leading-snug">
              Creates a database that updates itself. One-way — edits here are replaced on the next sync.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                disabled={isImporting}
                onClick={() => openSyncPicker("jira")}
                className="flex items-start gap-3 p-3 rounded-xl border border-component hover:border-zinc-300 dark:hover:border-zinc-600 hover-component transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-blue-50 dark:bg-blue-950/30">
                  <BrandIcon id="jira" alt="Jira" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Jira</p>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 leading-snug mt-0.5">Mirror a project, kept up to date</p>
                </div>
              </button>
              <button
                type="button"
                disabled={isImporting}
                onClick={() => openSyncPicker("gitlab")}
                className="flex items-start gap-3 p-3 rounded-xl border border-component hover:border-zinc-300 dark:hover:border-zinc-600 hover-component transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-orange-50 dark:bg-orange-950/30">
                  <BrandIcon id="gitlab" alt="GitLab" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">GitLab</p>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 leading-snug mt-0.5">Mirror a project, kept up to date</p>
                </div>
              </button>
              <button
                type="button"
                disabled={isImporting}
                onClick={() => openSyncPicker("asana")}
                className="flex items-start gap-3 p-3 rounded-xl border border-component hover:border-zinc-300 dark:hover:border-zinc-600 hover-component transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-rose-50 dark:bg-rose-950/30">
                  <BrandIcon id="asana" alt="Asana" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Asana</p>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 leading-snug mt-0.5">Mirror a project, kept up to date</p>
                </div>
              </button>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wide mb-3">
              File-based imports
            </p>
            <div className="grid grid-cols-2 gap-2">
              {FORMAT_CARDS.map(card => {
                const Icon = card.icon;
                return (
                  <button
                    key={card.id}
                    type="button"
                    disabled={isImporting}
                    onClick={() => cardInputRefs.current[card.id]?.click()}
                    className="flex items-start gap-3 p-3 rounded-xl border border-component hover:border-zinc-300 dark:hover:border-zinc-600 hover-component  transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${card.iconBg}`}>
                      <Icon className={`h-3.5 w-3.5 ${card.iconColor}`} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{card.label}</p>
                      <p className="text-xs text-zinc-500 dark:text-zinc-400 leading-snug mt-0.5">{card.description}</p>
                    </div>
                    <input
                      ref={el => { cardInputRefs.current[card.id] = el; }}
                      type="file"
                      accept={card.accept}
                      className="hidden"
                      onChange={e => { const f = e.target.files?.[0]; if (f) pickFile(f); e.target.value = ""; }}
                    />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
